import { useState } from "react"
import { Box, Collapse, Divider, Stack, Typography } from "@mui/material"
import { formatDistanceToNow } from "date-fns"
import createAvatar from "minimals-template/components/utils/createAvatar"
import Avatar from "minimals-template/components/Avatar"
import { Bound } from "lib/@components/binding/Bound"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { Repeat } from "lib/@components/repeat"
import { ListItemBox } from "lib/@components/ListItemBox"
import { If } from "lib/@components/switch"
import { RequestedFieldValueRow } from "./requested-field-value-row"
import { countAnsweredFields } from "./requested-information-count"

const EMPTY_FIELDS = []
const EMPTY_VALUES = {}

function fieldKey(field, index) {
    return field?.id ?? index
}

/**
 * One recipient's response to a share link, shown read-only on the internal reviewer's regime
 * "Responses" tab. A collapsed row (avatar + name/email + a badge counting how many requested fields
 * this recipient answered) that expands to reveal one row per requested field, each pairing the field
 * with this response's value. The link's `requestedFields` come from the surrounding `<Bound>` (set by
 * `ResponsesGroup`); the response's `values` are re-bound here so each `RequestedFieldValueRow` can read
 * its own field's answer.
 *
 * @param {object} props
 * @param {{ _id: string, createdBy?: string, authorName?: string, authorEmail?: string, regimeVersion?: string, values?: object, modified?: string, created?: string }} props.response
 */
export function ResponseCard({ response }) {
    const { requestedFields } = useBoundContext()
    const [expanded, setExpanded] = useState(false)
    if (!response) return null

    const fields = Array.isArray(requestedFields) ? requestedFields : EMPTY_FIELDS
    const answeredCount = countAnsweredFields(fields, getValues())

    return (
        <Box
            data-cy={`share-link-response-card-${response.createdBy ?? response._id}`}
            sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}
        >
            <ListItemBox
                onClick={toggle}
                data-cy="share-link-response-row"
                sx={{
                    gap: 1.5,
                    py: 1,
                    px: 1.5,
                    cursor: "pointer",
                    borderRadius: 1,
                    "&:hover": { bgcolor: "action.hover" },
                }}
            >
                <Avatar
                    alt={getAuthorName()}
                    sx={{ width: 36, height: 36, color: "#333", backgroundColor: getAvatarColor() }}
                >
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        {getInitials()}
                    </Typography>
                </Avatar>
                <Stack sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" noWrap>
                        {getAuthorName()}
                    </Typography>
                    <If truthy={!!getAuthorEmail()}>
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {getAuthorEmail()}
                        </Typography>
                    </If>
                    <If truthy={!!getRegimeVersionLabel()}>
                        <Typography
                            variant="caption"
                            color="primary.main"
                            data-cy="share-link-response-regime-version"
                            sx={{ fontWeight: 700 }}
                        >
                            {getRegimeVersionLabel()}
                        </Typography>
                    </If>
                </Stack>
                <Box
                    component="span"
                    data-cy="share-link-response-answered-count"
                    sx={{
                        minWidth: 24,
                        height: 22,
                        px: 0.75,
                        borderRadius: 999,
                        bgcolor: "action.selected",
                        color: "text.primary",
                        fontSize: 12,
                        fontWeight: 700,
                        lineHeight: "22px",
                        textAlign: "center",
                        flexShrink: 0,
                    }}
                >
                    {answeredCount}
                </Box>
            </ListItemBox>
            <Collapse in={expanded} unmountOnExit>
                <Stack spacing={1.5} sx={{ px: 1.5, pt: 0.5, pb: 1.5 }}>
                    <ListItemBox sx={{ justifyContent: "flex-end" }}>
                        <Typography variant="caption" color="text.secondary">
                            {getRelativeTime()}
                        </Typography>
                    </ListItemBox>
                    <Divider />
                    <Bound values={getValues()}>
                        <Stack spacing={1.5}>
                            <Repeat
                                list={fields}
                                pass="field"
                                keyFn={fieldKey}
                                item={<RequestedFieldValueRow />}
                                fallback={
                                    <Typography variant="body2" color="text.secondary">
                                        No information was requested.
                                    </Typography>
                                }
                            />
                        </Stack>
                    </Bound>
                </Stack>
            </Collapse>
        </Box>
    )

    function toggle() {
        setExpanded((current) => !current)
    }

    function getAuthorName() {
        return response.authorName?.trim() || "Unknown"
    }

    function getAuthorEmail() {
        return response.authorEmail?.trim() || ""
    }

    function getRegimeVersionLabel() {
        return response.regimeVersion?.trim() || ""
    }

    function getInitials() {
        return createAvatar(getAuthorName()).name
    }

    function getAvatarColor() {
        return createAvatar(getAuthorName()).color
    }

    function getValues() {
        return response.values ?? EMPTY_VALUES
    }

    function getRelativeTime() {
        const raw = response.modified ?? response.created
        if (!raw) return ""
        const date = new Date(raw)
        if (Number.isNaN(date.getTime())) return ""
        try {
            return formatDistanceToNow(date, { addSuffix: true })
        } catch {
            return date.toLocaleDateString()
        }
    }
}
